import { useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import './DotBackground.scss';

const DotBackground = () => {
  const { theme } = useTheme();
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let particles = [];
    let width, height;
    const isDark = theme === 'dark';
    const bgColor = isDark ? '#000000' : '#ffffff';
    const GRID_SPACING = 40;

    const buildParticles = () => {
      particles = [];
      const cols = Math.ceil(width / GRID_SPACING) + 1;
      const rows = Math.ceil(height / GRID_SPACING) + 1;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          particles.push({ baseX: c * GRID_SPACING, baseY: r * GRID_SPACING, x: c * GRID_SPACING, y: r * GRID_SPACING, size: 1.2 });
        }
      }
    };

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      buildParticles();
    };

    resize();

    const draw = () => {
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, width, height);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const INFLUENCE_RADIUS = 140;
      const LINE_DIST = 80;

      for (const p of particles) {
        const dx = mx - p.baseX;
        const dy = my - p.baseY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < INFLUENCE_RADIUS) {
          const force = (1 - dist / INFLUENCE_RADIUS) * 18;
          p.x = p.baseX - (dx / dist) * force;
          p.y = p.baseY - (dy / dist) * force;
          p.size = 1.2 + (1 - dist / INFLUENCE_RADIUS) * 2.5;
        } else {
          p.x += (p.baseX - p.x) * 0.08;
          p.y += (p.baseY - p.y) * 0.08;
          p.size += (1.2 - p.size) * 0.08;
        }

        const proximity = dist < INFLUENCE_RADIUS ? 1 - dist / INFLUENCE_RADIUS : 0;
        const dotAlpha = isDark ? 0.08 + proximity * 0.55 : 0.1 + proximity * 0.45;
        ctx.fillStyle = isDark
          ? `rgba(100, 220, 220, ${dotAlpha})`
          : `rgba(126, 5, 5, ${dotAlpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }

      if (mx > 0 && my > 0) {
        for (let i = 0; i < particles.length; i++) {
          const a = particles[i];
          const dxa = mx - a.x;
          const dya = my - a.y;
          const distA = Math.sqrt(dxa * dxa + dya * dya);
          if (distA > INFLUENCE_RADIUS) continue;
          for (let j = i + 1; j < particles.length; j++) {
            const b = particles[j];
            const dxb = b.x - a.x;
            const dyb = b.y - a.y;
            const distAB = Math.sqrt(dxb * dxb + dyb * dyb);
            if (distAB < LINE_DIST) {
              const lineAlpha = (1 - distAB / LINE_DIST) * (1 - distA / INFLUENCE_RADIUS) * (isDark ? 0.25 : 0.2);
              ctx.strokeStyle = isDark
                ? `rgba(100, 220, 220, ${lineAlpha})`
                : `rgba(126, 5, 5, ${lineAlpha})`;
              ctx.lineWidth = 0.6;
              ctx.beginPath();
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(b.x, b.y);
              ctx.stroke();
            }
          }
        }
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [theme]);

  useEffect(() => {
    const cleanup = initCanvas();
    return cleanup;
  }, [initCanvas]);

  useEffect(() => {
    const handleMouseMove = (e) => { mouseRef.current = { x: e.clientX, y: e.clientY }; };
    const handleMouseLeave = () => { mouseRef.current = { x: -1000, y: -1000 }; };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="dot-bg" />;
};

export default DotBackground;
